import React, { useState, useEffect, ReactNode } from "react";
import { Grid, Button, Paper } from "@material-ui/core";
import SwipeableViews from "react-swipeable-views";
import { Statement } from "../../firebase/statements";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import { colors } from "../../theme";
import { useTranslation } from "react-i18next";
import {
  VoteType,
  AGREE,
  DISAGREE,
  NO_OPINION,
  getStatementVote,
  voteStatement,
} from "../../firebase/voting";
import { useAuth } from "../../firebase/AuthProvider";
import Following from "./Following";
import DisagreedIcon from "./svg/icon-mobile-disagreed.svg";
import AgreedIcon from "./svg/icon-mobile-agreed.svg";
import TransparentPlaceholderIcon from "./svg/icon-mobile-transparent.svg";
import SignInDialog from "../signin/SigninDialog";
import StatementLink from "../shared/StatementLink";

interface Props {
  statement: Statement;
  titleIsLink: boolean;
  statementNavigation: ReactNode;
  backgroundImageUrl: string | null;
}

const useStyles = makeStyles((theme) => ({
  statementNavigationContainer: {
    backgroundImage:
      "linear-gradient(to bottom, " +
      colors.STATEMENT_NAV_BG +
      ", rgba(35, 31, 32, 0) 99%)",
    position: "absolute",
    zIndex: 1,
    width: "100%",
    height: "65px",
    padding: "10px 10px 0px 10px",
  },
  slide: {
    height: "419px",
    textAlign: "center",
    borderRadius: "0",
    width: "100%",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    position: "relative",
    top: "-419px",
    marginBottom: "-419px",
    zIndex: 2,
  },
  headerBackground: {
    height: "419px",
    width: "100%",
    zIndex: 1,
    borderRadius: "10px",
    position: "relative",
  },
  agree: {
    backgroundSize: "cover",
  },
  noVote: {
    backgroundSize: "cover",
  },
  disagree: {
    backgroundSize: "cover",
  },
  noOpinion: {
    backgroundSize: "cover",
  },
  statementTitle: {
    height: "245px",
    padding: "10px 5px 0px 5px",
    color: colors.STATEMENT_TITLE,
    fontFamily: "Poppins",
    fontSize: "25px",
    fontWeight: 900,
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "1.38",
    letterSpacing: "normal",
    textAlign: "center",
    textShadow: "1px 1px #000",
    position: "relative",
    zIndex: 4,
    top: "40px",
  },
  statementLink: {
    position: "relative",
    zIndex: 4,
    top: "40px",
    color: colors.STATEMENT_TITLE,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "none",
    },
  },
  button: {
    border: "1px solid " + colors.TEXT_SECONDARY,
    borderRadius: "0",
    width: "100%",
    height: "52px",
    textTransform: "none",
    color: colors.FOLLOW_BUTTON_TEXT,
    fontSize: "14px",
    backgroundColor: colors.FOLLOW_BUTTON_BG,
    fontWeight: 600,
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "normal",
    letterSpacing: "-0.03px",
    padding: "15px",
    "&:hover": {
      background: colors.FOLLOW_BUTTON_BG,
    },
  },
  noLeftBorder: {
    borderLeft: "0",
  },
  voteHintContainer: {
    color: colors.VOTE_SWIPE_HINT,
  },
  voteHintText: {
    fontSize: "16px",
    fontWeight: "normal",
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "normal",
    letterSpacing: "normal",
    textAlign: "center",
    padding: "15px",
    [theme.breakpoints.down("xs")]: {
      lineHeight: "28px",
    },
  },
  voteHintIcon: {
    paddingTop: "3px",
  },
  icon: {
    paddingTop: "70px",
  },
  imgVote: {
    position: "relative",
    top: "120px",
    zIndex: 5,
  },
}));

export default function HeaderMobile({
  statement,
  titleIsLink,
  statementNavigation,
  backgroundImageUrl,
}: Props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [vote, setVote] = useState<VoteType | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openSignInDialog, setOpenSignInDialog] = useState(false);

  useEffect(() => {
    if (!user || !statement) return;
    const unsubscribe = getStatementVote(statement.id, user.id, setVote);
    return () => unsubscribe();
  }, [user, statement]);

  async function submitVote(index: number) {
    if (!user) {
      setOpenSignInDialog(true);
      return;
    }

    const vote: VoteType = index === 0 ? DISAGREE : AGREE;

    setVote(vote);
    await saveVote(vote);
  }

  async function saveVote(vote: VoteType) {
    if (!user) {
      setOpenSignInDialog(true);
      return;
    }

    setIsLoading(true);
    await voteStatement(statement, user, vote);
    setIsLoading(false);
  }

  const notYetVotedComponent = (
    <>
      <div className={classes.headerBackground} />
      <Paper
        className={clsx(classes.slide, classes.noVote)}
        key="noVote"
        style={{
          backgroundImage: `url(${backgroundImageUrl})`,
          opacity: "80%",
        }}
      >
        <div className={classes.statementTitle}>
          <StatementLink
            slug={statement.slug}
            className={classes.statementLink}
            clickable={titleIsLink}
          >
            {statement.title}
          </StatementLink>
        </div>
        <img
          src={TransparentPlaceholderIcon}
          alt={t("voting.disagreed")}
          className={classes.imgVote}
        />
      </Paper>
    </>
  );

  const agreeComponent = (
    <>
      <div
        className={classes.headerBackground}
        style={{ backgroundColor: colors.AGREED }}
      />
      <Paper
        className={clsx(classes.slide, classes.agree)}
        key={AGREE}
        style={{
          backgroundImage: `url(${backgroundImageUrl})`,
          opacity: "80%",
        }}
      >
        <div className={classes.statementTitle}>
          <StatementLink
            slug={statement.slug}
            className={classes.statementLink}
            clickable={titleIsLink}
          >
            {statement.title}
          </StatementLink>
        </div>
        <img
          src={AgreedIcon}
          alt={t("voting.disagreed")}
          className={classes.imgVote}
        />
      </Paper>
    </>
  );

  const disagreeComponent = (
    <>
      <div
        className={classes.headerBackground}
        style={{ backgroundColor: colors.DISAGREE }}
      />
      <Paper
        className={clsx(classes.slide, classes.disagree)}
        key={DISAGREE}
        style={{
          backgroundImage: `url(${backgroundImageUrl})`,
          opacity: "80%",
        }}
      >
        <div className={classes.statementTitle}>
          <StatementLink
            slug={statement.slug}
            className={classes.statementLink}
            clickable={titleIsLink}
          >
            {statement.title}
          </StatementLink>
        </div>
        <img
          src={DisagreedIcon}
          alt={t("voting.disagreed")}
          className={classes.imgVote}
        />
      </Paper>
    </>
  );

  const noOpinionComponent = (
    <>
      <div className={classes.headerBackground} />
      <Paper
        className={clsx(classes.slide, classes.noOpinion)}
        key={NO_OPINION}
        style={{
          backgroundImage: `url(${backgroundImageUrl})`,
          opacity: "80%",
        }}
      >
        <>
          <div className={classes.statementTitle}>
            <StatementLink
              slug={statement.slug}
              className={classes.statementLink}
              clickable={titleIsLink}
            >
              {statement.title}
            </StatementLink>
          </div>
          <img
            src={AgreedIcon}
            alt={t("voting.agreed")}
            style={{ marginRight: "3px" }}
            className={classes.imgVote}
          />
          <img
            src={DisagreedIcon}
            alt={t("voting.disagreed")}
            style={{ marginLeft: "3px" }}
            className={classes.imgVote}
          />
        </>
      </Paper>
    </>
  );

  // If not voted yet or voted with "no opinion", there are 3 "slides": disagree to the left, the statement and agree to the right.
  // When already voted, there are only 2 "slides": disagree and agree.
  let voteIndex = 1;
  let votingComponents = [disagreeComponent, agreeComponent];
  switch (vote) {
    case AGREE:
      break;
    case DISAGREE:
      voteIndex = 0;
      break;
    case NO_OPINION:
      votingComponents = [
        disagreeComponent,
        noOpinionComponent,
        agreeComponent,
      ];
      break;
    default:
      votingComponents = [
        disagreeComponent,
        notYetVotedComponent,
        agreeComponent,
      ];
      break;
  }

  return (
    <>
      <SignInDialog
        open={openSignInDialog}
        onClose={() => setOpenSignInDialog(false)}
      />
      <Grid container item xs={12}>
        <Grid item xs={12} style={{ position: "relative" }}>
          <Grid item xs={12} className={classes.statementNavigationContainer}>
            {statementNavigation}
          </Grid>
          {vote === AGREE || vote === DISAGREE ? (
            <SwipeableViewsWrapper_1
              voteIndex={vote === AGREE ? 1 : 0}
              submitVote={submitVote}
              isLoading={isLoading}
            >
              {votingComponents.map((votingComponent, index) => (
                <div key={index}>{votingComponent}</div>
              ))}
            </SwipeableViewsWrapper_1>
          ) : (
            <SwipeableViewsWrapper_2
              submitVote={submitVote}
              isLoading={isLoading}
            >
              {votingComponents.map((votingComponent, index) => (
                <div key={index}>{votingComponent}</div>
              ))}
            </SwipeableViewsWrapper_2>
          )}
        </Grid>
      </Grid>
      <Grid container item xs={12}>
        <Grid item xs={6}>
          <Button
            className={classes.button}
            onClick={async () => {
              await saveVote(NO_OPINION);
            }}
            disabled={vote === NO_OPINION}
          >
            {t("voting.noopinion")}
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Following
            statement={statement}
            className={clsx(classes.button, classes.noLeftBorder)}
          />
        </Grid>
      </Grid>
    </>
  );
}

// The number of children for the SwipeableViews component vary between renders (i.e. voting): either 2 or 3.
// But somehow the SwipeableViews component does not work with a variable number of children...
// As a workaround, there is a separate SwipeableViewsWrapper for both situations.
function SwipeableViewsWrapper_1({
  voteIndex,
  submitVote,
  isLoading,
  children,
}: {
  voteIndex: number;
  submitVote: any;
  isLoading: boolean;
  children: ReactNode[];
}) {
  return (
    <SwipeableViews
      enableMouseEvents
      index={voteIndex}
      onChangeIndex={submitVote}
      disabled={isLoading}
    >
      {children}
    </SwipeableViews>
  );
}

function SwipeableViewsWrapper_2({
  submitVote,
  isLoading,
  children,
}: {
  submitVote: any;
  isLoading: boolean;
  children: ReactNode[];
}) {
  return (
    <SwipeableViews
      enableMouseEvents
      index={1}
      onChangeIndex={submitVote}
      disabled={isLoading}
    >
      {children}
    </SwipeableViews>
  );
}
